import { Dayjs } from 'dayjs';
import { IProductWithSerialNumber } from '../../../contracts/product-with-serial-number.js';
import { Result } from '../../../utility/result.js';
import { ErrorLike } from '../../core/error-like.js';
import { DataModelBuilder, SerializationContext, SerializedData } from '../../dto/data-model.js';
import { BusinessModuleRef } from '../business-module-ref.js';
import { SerialNumberScope } from './serial-number-scope.js';
import { SerialNumberStrategy } from './serial-number-strategy.js';
import { StandardSerialNumberPool } from './standard-serial-number-pool.js';
import { EventTicket } from '../event-ticket.js';
import { OpenAccessPass } from '../open-access-pass/open-access-pass.js';
import { GiftCard } from '../gift-card/gift-card.js';
import { UnsupportedProductType } from './unsupported-product-type.js';
import { MapUtils } from '../../../utility/map-utils.js';
import { fromEnumAsString, toEnumFromString } from '../../../serialization/enum-utils.js';
import { JSONUtils } from '../../../utility/json-utils.js';
import { cloneMap, isSameData, isSameMap, mapToData } from '../../dto/data-model-utils.js';
import { TimeZone } from '../../../time/timezone.js';
import { LegacyTicketBasedSerialNumberPool } from './legacy-ticket-based-serial-number-pool.js';
import { EventProfile } from '../event-profile.js';
import { TrueTime } from '../../../time/true-time.js';
import { getTicketTypeShortCode } from '../ticket-type.js';
import { VersionRange } from '../../core/version-range.js';

export class TemplateBasedSerialNumberPool extends StandardSerialNumberPool {
  static readonly TYPE_ID: string = 'fd55a887385d4dd695eb9465e7e42b09';
  static readonly SCHEMA_VERSION: number = 2;

  private constructor(
    public readonly businessRef: BusinessModuleRef,
    public readonly eventId: string | null,
    public readonly seasonId: string | null,
    public readonly creationDate: Dayjs,
    public readonly shortCode: string,
    public readonly scope: SerialNumberScope,
    protected readonly serialNumberBuckets: Map<string, number>,
    private readonly groups: Map<string, number>,
  ) {
    super();
  }

  static create(args: {
    businessRef: BusinessModuleRef,
    eventId: string | null,
    seasonId: string | null,
    creationDate: Dayjs,
    shortCode: string,
    scope: SerialNumberScope,
    serialNumberBuckets: Map<string, number>,
    groups: Map<string, number>,
  }): TemplateBasedSerialNumberPool {
    return new TemplateBasedSerialNumberPool(
        args.businessRef,
        args.eventId,
        args.seasonId,
        args.creationDate,
        args.shortCode,
        args.scope,
        args.serialNumberBuckets,
        args.groups,
    );
  }

  get $typeId(): string {
    return TemplateBasedSerialNumberPool.TYPE_ID;
  }

  get $schemaVersion(): VersionRange {
    return VersionRange.between(1, TemplateBasedSerialNumberPool.SCHEMA_VERSION);
  }

  get strategy(): SerialNumberStrategy {
    return SerialNumberStrategy.AssignByTemplate;
  }

  static fromLegacy(legacyPool: LegacyTicketBasedSerialNumberPool): TemplateBasedSerialNumberPool {
    return TemplateBasedSerialNumberPool.create({
      businessRef: legacyPool.businessRef.clone(),
      eventId: legacyPool.eventId,
      seasonId: null,
      creationDate: TimeZone.cloneDate(legacyPool.creationDate),
      scope: legacyPool.scope,
      groups: cloneMap(legacyPool.getTicketGroups(), (v) => v),
      serialNumberBuckets: cloneMap(legacyPool.getSerialNumbers(), (v) => v),
      shortCode: legacyPool.shortCode,
    });
  }

  static forBusiness(businessRef: BusinessModuleRef, shortCode: string): TemplateBasedSerialNumberPool {
    return TemplateBasedSerialNumberPool.create({
      businessRef: businessRef,
      eventId: null,
      seasonId: null,
      creationDate: TrueTime.now(),
      shortCode: shortCode,
      scope: SerialNumberScope.Business,
      groups: new Map<string, number>(),
      serialNumberBuckets: new Map<string, number>(),
    });
  }

  static forEvent(event: EventProfile): TemplateBasedSerialNumberPool | null {
    if(event.shortCode == null) {
      return null;
    }

    return TemplateBasedSerialNumberPool.create({
      businessRef: event.businessRef,
      eventId: event.uniqueId,
      seasonId: null,
      creationDate: TrueTime.now(),
      shortCode: event.shortCode,
      scope: SerialNumberScope.Event,
      groups: new Map<string, number>(),
      serialNumberBuckets: new Map<string, number>(),
    });
  }

  override getSerialNumber(product: IProductWithSerialNumber): Result<string, ErrorLike> {
    return this.ensureProductGroupIsRegistered(product)
        .chain(() => super.getSerialNumber(product));
  }

  protected getFormattedSeasonId(product: IProductWithSerialNumber): Result<string | null, ErrorLike> {
    return Result.ok(this.seasonId);
  }

  private ensureProductGroupIsRegistered(product: IProductWithSerialNumber): Result<null, ErrorLike> {
    return this.getProductGroupId(product).chain((groupId) => {
      this.registerGroup(groupId);

      return Result.ok(null);
    });
  }

  private registerGroup(groupId: string): void {
    if(this.groups.has(groupId)) {
      return;
    }

    const nextGroupNumber = this.getNextGroupNumber();
    this.groups.set(groupId, nextGroupNumber);
  }

  private getNextGroupNumber(): number {
    return this.groups.size + 1;
  }

  private getProductGroupId(product: IProductWithSerialNumber): Result<string, ErrorLike> {
    switch(true) {
      case product instanceof EventTicket: {
        return Result.ok(product.templateId);
      } break;
      case product instanceof OpenAccessPass: {
        return Result.ok(product.templateId);
      } break;
      case product instanceof GiftCard: {
        return Result.ok(product.templateId);
      } break;
      default: {
        return Result.error(new UnsupportedProductType(product));
      } break;
    }
  }

  protected getFormattedProductGroupId(product: IProductWithSerialNumber): Result<string, ErrorLike> {
    return this.getProductGroupId(product).chain((groupId) => {
      const result = this.groups.get(groupId);
      if(result == null) {
        return Result.error(`Cannot find group with id {${groupId}}.`);
      }

      return Result.ok(result.toFixed().padStart(3, '0'));
    });
  }

  protected getProductSerialNumberBucketId(
      product: IProductWithSerialNumber,
  ): Result<string, ErrorLike> {
    switch(true) {
      case product instanceof EventTicket: {
        const shortCode = getTicketTypeShortCode(product.ticketType);

        return Result.ok(`${shortCode}_${product.templateId}`);
      } break;
      case product instanceof OpenAccessPass: {
        return Result.ok(`p_${product.templateId}`);
      } break;
      case product instanceof GiftCard: {
        return Result.ok(`g_${product.templateId}`);
      } break;
      default: {
        return Result.error(new UnsupportedProductType(product));
      } break;
    }
  }

  static fromMap(context: SerializationContext, map: SerializedData): TemplateBasedSerialNumberPool {
    return TemplateBasedSerialNumberPool.create({
      businessRef: BusinessModuleRef.mustDecode(MapUtils.getString(map, 'businessRef')),
      eventId: MapUtils.getStringOrNull(map, 'eventId'),
      seasonId: MapUtils.getStringOrNull(map, 'seasonId'),
      creationDate: context.decodeDate(MapUtils.getNumber(map, 'creationDate')),
      scope: toEnumFromString(MapUtils.getString(map, 'scope'), SerialNumberScope),
      shortCode: MapUtils.getString(map, 'shortCode'),
      groups: MapUtils.getMap(map, 'groups', (k, v) => ({
        key: JSONUtils.decodeString(k),
        value: JSONUtils.decodeNumber(v),
      })),
      serialNumberBuckets: MapUtils.getMap(map, 'serialNumberBuckets', (k, v) => ({
        key: JSONUtils.decodeString(k),
        value: JSONUtils.decodeNumber(v),
      })),
    });
  }

  toMap(context: SerializationContext): SerializedData {
    return {
      businessRef: this.businessRef.encode(),
      eventId: this.eventId,
      seasonId: this.seasonId,
      creationDate: context.encodeDate(this.creationDate),
      scope: fromEnumAsString(this.scope),
      shortCode: this.shortCode,
      groups: mapToData(this.groups, (k, v) => ({
        key: k,
        value: v,
      })),
      groupCount: this.groups.size,
      serialNumberBuckets: mapToData(this.serialNumberBuckets, (k, v) => ({
        key: k,
        value: v,
      })),
    };
  }

  clone(): this {
    return TemplateBasedSerialNumberPool.create({
      businessRef: this.businessRef.clone(),
      eventId: this.eventId,
      seasonId: this.seasonId,
      creationDate: TimeZone.cloneDate(this.creationDate),
      scope: this.scope,
      shortCode: this.shortCode,
      groups: cloneMap(this.groups, (v) => v),
      serialNumberBuckets: cloneMap(this.serialNumberBuckets, (v) => v),
    }) as this;
  }

  isSame(other: this): boolean {
    if(other === this) {
      return true;
    }

    return isSameData(other.businessRef, this.businessRef) &&
      other.eventId === this.eventId &&
      other.seasonId === this.seasonId &&
      other.creationDate === this.creationDate &&
      other.shortCode === this.shortCode &&
      other.scope === this.scope &&
      isSameMap(other.groups, this.groups, (lh, rh) => lh === rh) &&
      isSameMap(other.serialNumberBuckets, this.serialNumberBuckets, (lh, rh) => lh === rh);
  }

  resetForNewSeason(seasonId: string): this {
    return TemplateBasedSerialNumberPool.create({
      businessRef: this.businessRef.clone(),
      eventId: this.eventId,
      seasonId: seasonId,
      creationDate: TimeZone.cloneDate(this.creationDate),
      scope: this.scope,
      shortCode: this.shortCode,
      groups: new Map<string, number>(),
      serialNumberBuckets: new Map<string, number>(),
    }) as this;
  }
}

export class TemplateBasedSerialNumberPoolBuilder extends DataModelBuilder<TemplateBasedSerialNumberPool> {
  build(context: SerializationContext, map: SerializedData): TemplateBasedSerialNumberPool {
    return TemplateBasedSerialNumberPool.fromMap(context, map);
  }

  migrateToCurrentSchemaVersion(context: SerializationContext, map: SerializedData): SerializedData | null {
    const versionRange = MapUtils.getVersionRange(map);

    if(versionRange.contains(TemplateBasedSerialNumberPool.SCHEMA_VERSION)) {
      return map;
    }
    if(versionRange.isGreaterThan(TemplateBasedSerialNumberPool.SCHEMA_VERSION)) {
      return null; // This data comes from the FUTURE. We have no idea how to migrate it.
    }

    let version = versionRange.to;

    // in this version we added `seasonId` of type `number | null`
    if(version < 2) version = 2;

    map['$schema_version'] = VersionRange.encodedExactly(version);
    return map;
  }
}
